import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext } from "@/App";
import { 
  Bot, 
  Sparkles, 
  ArrowRight,
  Flame,
  Star,
  BookOpen,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Welcome = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showMessage, setShowMessage] = useState(0);

  const firstName = user?.profile?.first_name || user?.name?.split(" ")[0] || "Estudiante";

  const messages = [
    {
      text: `¡Hola ${firstName}! Soy QHUB.BUDDY, tu tutor virtual.`,
      delay: 0
    },
    {
      text: "Estoy aquí para ayudarte a entender los contenidos de cada materia, pero de una forma diferente...",
      delay: 1500
    },
    {
      text: "No voy a darte todas las respuestas. En cambio, voy a hacerte preguntas que te ayuden a pensar y descubrir por ti mismo.",
      delay: 3500
    },
    {
      text: "En cada materia encontrarás una ruta de aprendizaje. Yo te acompañaré paso a paso.",
      delay: 6000
    },
    {
      text: "¿Listo para empezar? ¡Vamos!",
      delay: 8000
    }
  ];

  useEffect(() => {
    messages.forEach((_, index) => {
      setTimeout(() => {
        setShowMessage(index + 1);
      }, messages[index].delay);
    });
  }, []);

  return (
    <div className="min-h-screen bg-mesh flex flex-col" data-testid="welcome-page">
      {/* Header with stats preview */}
      <header className="p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg"
              animate={{ 
                scale: [1, 1.1, 1],
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
              }}
            >
              <Bot className="w-6 h-6 text-white" />
            </motion.div>
            <div>
              <img src="/qhub-buddy-logo.png" alt="QHUB.BUDDY" className="h-8 w-auto" />
              <p className="text-xs text-gray-500 mt-0.5">Tu tutor virtual</p>
            </div>
          </div>
          
          {/* Stats preview */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-orange-600">1 día</span>
            </div>
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200">
              <Star className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-purple-600">50 pts</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {/* Chat-like messages */}
          <div className="space-y-4 mb-8">
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: showMessage > index ? 1 : 0,
                  y: showMessage > index ? 0 : 20
                }}
                className="flex gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shrink-0 shadow-md">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 bg-white rounded-2xl rounded-tl-sm p-4 shadow-sm border border-gray-100">
                  <p className="text-gray-700">{message.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Action cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: showMessage >= messages.length ? 1 : 0,
              y: showMessage >= messages.length ? 0 : 20
            }}
            className="space-y-4"
          >
            {/* Info cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-3">
                  <Flame className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Rachas</h3>
                <p className="text-sm text-gray-500">Mantén tu racha estudiando cada día</p>
              </div>
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center mb-3">
                  <Star className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Puntos</h3>
                <p className="text-sm text-gray-500">Gana puntos por cada actividad</p>
              </div>
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">4 Materias</h3>
                <p className="text-sm text-gray-500">Rutas de aprendizaje completas</p>
              </div>
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center mb-3">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Acompañamiento</h3>
                <p className="text-sm text-gray-500">Preguntas que te hacen pensar</p>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full h-14 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white font-medium text-base flex items-center justify-center gap-3 shadow-lg hover:opacity-90 transition-all"
              data-testid="start-learning-btn"
            >
              Explorar mis materias
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Welcome;
